// Copyright 2018 Sorama B.V.

import { autoinject, computedFrom, observable } from "aurelia-framework";
import { Point3D } from "datatypes/SoramaWebApi";
import { IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { MotionDevice } from "./MotionDeviceDiscoveryService";

@autoinject
export class MotionDeviceService {
    private webrequestHandler: IWebRequestHandler;

    @observable
    public connectedMotionDevice: MotionDevice;
    @observable
    public moveObjectInsteadOfArray = false;

    public lastKnownPos: Point3D;
    public currentGoalPos: Point3D;
    private timerId: NodeJS.Timeout;

    @observable private _isActive = false;

    @computedFrom("_isActive")
    public get isActive(): boolean {
        return this._isActive;
    }

    constructor(webrequestHandler: IWebRequestHandler) {
        this.webrequestHandler = webrequestHandler;
    }

    private connectedMotionDeviceChanged(): void {
        if (!this.connectedMotionDevice) return;

        this.lastKnownPos = { x: 0, y: 0, z: 0 };
        this.currentGoalPos = undefined;
        this.currentPosition();
    }

    private moveObjectInsteadOfArrayChanged(): void {
        if (!this.connectedMotionDevice) return;

        this.currentPosition();
    }

    public currentPosition(): Promise<Point3D> {
        if (!this.connectedMotionDevice) {
            throw new Error("No MotionDevice connected");
        }

        return this.webrequestHandler
            .createGetRequest<Point3D>(
                this.connectedMotionDevice.baseApi + "/" + this.connectedMotionDevice.name + "/Position"
            )
            .send()
            .then((x) => x.content)
            .then((x) => {
                this.lastKnownPos = this.fromMotionSystemCoordinates(x);
                return this.lastKnownPos;
            })
            .catch(() => {
                // the current position is a optional call. So if it fails, just return the last known position
                return this.lastKnownPos;
            });
    }

    public moveTo(point: Point3D): Promise<void> {
        if (!this.connectedMotionDevice) {
            this._isActive = false;
            throw new Error("No MotionDevice connected");
        }

        this.currentGoalPos = point;
        this._isActive = true;

        const motionDevicePoint = this.toMotionSystemCoordinates(point);

        return this.webrequestHandler
            .createPutRequest<Point3D>(
                this.connectedMotionDevice.baseApi + "/" + this.connectedMotionDevice.name + "/Position",
                motionDevicePoint
            )
            .send()
            .then(() => {
                this._isActive = false;
                this.lastKnownPos = point;
                this.currentGoalPos = undefined;
            })
            .catch((e) => {
                this.currentGoalPos = undefined;
                this._isActive = false;
                throw e;
            });
    }

    public stop(): Promise<void> {
        if (!this.connectedMotionDevice) {
            throw new Error("No MotionDevice connected");
        }

        return this.webrequestHandler
            .createPutRequest(this.connectedMotionDevice.baseApi + "/" + this.connectedMotionDevice.name + "/Stop")
            .send()
            .then(() => {
                this.currentGoalPos = undefined;
                this._isActive = false;
                this.currentPosition();
            })
            .catch((e) => {
                this._isActive = false;
                this.currentGoalPos = undefined;
                throw e;
            });
    }

    public home(): Promise<void> {
        if (!this.connectedMotionDevice) {
            throw new Error("No MotionDevice connected");
        }

        this._isActive = true;

        this.currentGoalPos = { x: 0, y: 0, z: 0 };

        return this.webrequestHandler
            .createPutRequest(this.connectedMotionDevice.baseApi + "/" + this.connectedMotionDevice.name + "/Home")
            .send()
            .then(() => {
                this.lastKnownPos = { x: 0, y: 0, z: 0 };
                this.connectedMotionDevice.isHomed = true;
                this._isActive = false;
                this.currentGoalPos = undefined;
            })
            .catch((e) => {
                this._isActive = false;
                this.currentGoalPos = undefined;
                throw e;
            });
    }

    private _isActiveChanged(): void {
        if (!this._isActive) {
            if (this.timerId) {
                clearTimeout(this.timerId);
                this.timerId = undefined;
            }
            return;
        }

        if (this._isActive == true) {
            this.currentPosition()
                .then((x) => {
                    this.lastKnownPos = x;
                })
                .then(() => {
                    this.timerId = setTimeout(() => {
                        this._isActiveChanged();
                    }, 1000);
                })
                .catch(() => {
                    this._isActive = false;
                    if (this.timerId) {
                        clearTimeout(this.timerId);
                        this.timerId = undefined;
                    }
                });
        }
    }

    private toMotionSystemCoordinates(point: Point3D): Point3D {
        if (!this.moveObjectInsteadOfArray) return point;

        return <Point3D>{
            x: this.connectedMotionDevice.range.x - point.x,
            y: this.connectedMotionDevice.range.y - point.y,
            z: point.z,
        };
    }

    private fromMotionSystemCoordinates(point: Point3D): Point3D {
        if (!this.moveObjectInsteadOfArray) return point;

        return <Point3D>{
            x: this.connectedMotionDevice.range.x - point.x,
            y: this.connectedMotionDevice.range.y - point.y,
            z: point.z,
        };
    }
}
